import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  DollarSign,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  Building2,
  Edit,
  Loader2,
} from 'lucide-react';
import Button from '@/components/common/Button';
import { recruiterJobOfferService } from '@/services/jobOfferService';
import { JobOffer } from '@/types';
import { formatEmploymentInfo } from '@/utils/jobOfferUtils';
import { toast } from '@/stores/toastStore';

export default function SentJobOfferDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('jobOffers');

  const [loading, setLoading] = useState(true);
  const [offer, setOffer] = useState<JobOffer | null>(null);

  useEffect(() => {
    if (!id) return;
    recruiterJobOfferService.getJobOfferDetails(id)
      .then(setOffer)
      .catch(() => {
        toast.error('Failed to load job offer');
        navigate('/recruiter/job-offers');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const formatDate = (s: string) => new Date(s).toLocaleDateString();
  const formatDateTime = (s: string) => new Date(s).toLocaleString();

  const formatSalary = (min?: number, max?: number, curr?: string) => {
    const currency = curr || 'USD';
    if (min && max) return `${min.toLocaleString()} – ${max.toLocaleString()} ${currency}`;
    if (min) return `${min.toLocaleString()}+ ${currency}`;
    if (max) return `Up to ${max.toLocaleString()} ${currency}`;
    return null;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'VIEWED': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ACCEPTED': return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECTED': return 'bg-red-100 text-red-800 border-red-200';
      case 'WITHDRAWN': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const canEdit = offer?.status === 'PENDING' || offer?.status === 'VIEWED';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!offer) return null;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="secondary"
          onClick={() => navigate('/recruiter/job-offers')}
          className="mb-4 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Job Offers
        </Button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{offer.position}</h1>
            <p className="text-gray-600 mt-1">
              {t('offers.details.sentTo')}: {offer.candidate?.firstName} {offer.candidate?.lastName}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${getStatusColor(offer.status)}`}>
              {t(`offers.status.${offer.status.toLowerCase()}`)}
            </span>
            {canEdit && (
              <Link to={`/recruiter/job-offers/${offer.id}/edit`}>
                <Button variant="secondary" size="sm" className="flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Candidate Info */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <User className="w-4 h-4" />
            {t('offers.details.candidateInfo')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">{t('offers.details.name')}</p>
              <p className="font-medium text-gray-900">
                {offer.candidate?.firstName} {offer.candidate?.lastName}
              </p>
            </div>
            {offer.candidate?.email && (
              <div>
                <p className="text-xs text-gray-500">{t('offers.details.email')}</p>
                <p className="font-medium text-gray-900 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> {offer.candidate.email}
                </p>
              </div>
            )}
            {offer.candidate?.phone && (
              <div>
                <p className="text-xs text-gray-500">{t('offers.details.phone')}</p>
                <p className="font-medium text-gray-900 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {offer.candidate.phone}
                </p>
              </div>
            )}
            {offer.candidate?.jobTitle && (
              <div>
                <p className="text-xs text-gray-500">{t('offers.details.currentRole')}</p>
                <p className="font-medium text-gray-900">{offer.candidate.jobTitle}</p>
              </div>
            )}
          </div>
        </div>

        {/* Offer Details Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              <p className="text-xs text-gray-500">{t('offers.details.position')}</p>
            </div>
            <p className="font-medium text-gray-900">{offer.position}</p>
          </div>

          {offer.location && (
            <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                <p className="text-xs text-gray-500">{t('offers.details.location')}</p>
              </div>
              <p className="font-medium text-gray-900">{offer.location}</p>
            </div>
          )}

          {(offer.employmentType || offer.workMode) && (
            <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                <p className="text-xs text-gray-500">{t('offers.details.type')}</p>
              </div>
              <p className="font-medium text-gray-900">
                {formatEmploymentInfo(offer.employmentType, offer.workMode)}
              </p>
            </div>
          )}

          {formatSalary(offer.salaryMin, offer.salaryMax, offer.salaryCurrency) && (
            <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <p className="text-xs text-gray-500">{t('offers.details.salary')}</p>
              </div>
              <p className="font-medium text-gray-900">
                {formatSalary(offer.salaryMin, offer.salaryMax, offer.salaryCurrency)}
              </p>
            </div>
          )}

          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <p className="text-xs text-gray-500">{t('offers.details.sentOn')}</p>
            </div>
            <p className="font-medium text-gray-900">{formatDate(offer.createdAt)}</p>
          </div>

          {offer.expiresAt && (
            <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-blue-600" />
                <p className="text-xs text-gray-500">{t('offers.details.expiresOn')}</p>
              </div>
              <p className="font-medium text-gray-900">{formatDate(offer.expiresAt)}</p>
            </div>
          )}
        </div>

        {/* Message */}
        {offer.message && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('offers.details.message')}</h3>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{offer.message}</p>
          </div>
        )}

        {/* Responsibilities */}
        {offer.responsibilities && (
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('offers.details.responsibilities')}</h3>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{offer.responsibilities}</p>
          </div>
        )}

        {/* Requirements */}
        {offer.requirements && (
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('offers.details.requirements')}</h3>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{offer.requirements}</p>
          </div>
        )}

        {/* Benefits */}
        {offer.benefits && (
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('offers.details.benefits')}</h3>
            <p className="text-sm text-gray-900 whitespace-pre-wrap">{offer.benefits}</p>
          </div>
        )}

        {/* Timeline */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('offers.details.timeline')}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('offers.details.created')}</span>
              <span className="font-medium text-gray-900">{formatDateTime(offer.createdAt)}</span>
            </div>
            {offer.viewedAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t('offers.details.viewed')}</span>
                <span className="font-medium text-gray-900">{formatDateTime(offer.viewedAt)}</span>
              </div>
            )}
            {offer.respondedAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t('offers.details.responded')}</span>
                <span className="font-medium text-gray-900">{formatDateTime(offer.respondedAt)}</span>
              </div>
            )}
            {offer.updatedAt && offer.updatedAt !== offer.createdAt && (
              <div className="flex justify-between">
                <span className="text-gray-500">{t('offers.details.lastUpdated')}</span>
                <span className="font-medium text-gray-900">{formatDateTime(offer.updatedAt)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
